import React from "react";
import AccordionSummary from "@mui/material/AccordionSummary";
import {
  Accordion,
  IconButton,
  Card,
  Typography,
  Tooltip,
  Link,
  Stack,
  Chip,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

// Shown when a product has no image (or its image URL fails to load).
const DEFAULT_PRODUCT_IMAGE =
  "https://ik.imagekit.io/7s2whdfxn/Placeholders/placeholder-default.png";

const ProductItem = ({
  item,
  disabled,
  onDelete,
  onEdit,
  categories,
  productLanguages,
}) => {
  const { t, i18n } = useTranslation();
  const productCategories = item?.metadata?.category
    ?.split(",")
    .map((categoryId) => categories[categoryId]);
  return (
    <Card sx={{ mb: 1 }}>
      <Accordion expanded={false} square sx={{ borderRadius: 2 }}>
        <AccordionSummary
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          sx={{
            px: 3,
            flexDirection: "row-reverse",
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              justifyContent: "space-between",

              "&.Mui-expanded": {
                margin: "12px 0",
              },
            },
            ".MuiAccordionSummary-expandIconWrapper": {
              color: "text.secondary",
              transform: "none",
              height: 28,
              width: 28,
              alignItems: "center",
              justifyContent: "center",
              mr: 1,

              "&.Mui-expanded": {
                transform: "none",
                color: "primary.main",
                borderColor: "primary.main",
              },

              "& svg": {
                fontSize: "1.25rem",
              },
            },
          }}
        >
          <Div
            sx={{
              display: { xs: "none", md: "block" },
              width: "10%",
              flexShrink: 0,
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
            >
              {t("pages.presentation.id")}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant={"body1"}>{item.id}</Typography>
            </Stack>
          </Div>
          <Div sx={{ flex: { xs: "0 1 auto", md: 2 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.products.image")}
            </Typography>
            <Typography variant={"body1"}>
              <img
                src={item.metadata?.image || DEFAULT_PRODUCT_IMAGE}
                onError={(e) => {
                  if (e.currentTarget.src !== DEFAULT_PRODUCT_IMAGE) {
                    e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                  }
                }}
                width={75}
                alt={
                  item.metadata?.name?.[
                    productLanguages[0]?.code || i18n.language
                  ] || "Product image"
                }
              />
            </Typography>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 2 },
              flexShrink: 0,
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.products.productName")}
            </Typography>
            <Typography variant={"body1"}>
              {(item?.metadata?.name &&
                item?.metadata?.name[
                  productLanguages[0]?.code || i18n.language
                ]) ||
                ""}
            </Typography>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 2 },
              flexShrink: 0,
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.articles.category")}
            </Typography>
            {productCategories?.map((category, index) => {
              const name = category?.metadata?.label[i18n.language] || "";
              return name ? (
                <Chip
                  key={index}
                  label={name}
                  variant="outlined"
                  sx={{ mt: 0.5, mr: 0.5 }}
                />
              ) : null;
            })}
          </Div>
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 1 },
              flexShrink: 0,
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.products.price")}
            </Typography>
            <Typography variant={"body1"}>
              {item.metadata?.salePrice || item.metadata?.price || ""}
            </Typography>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 1 },
              flexShrink: 0,
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.screens.status")}
            </Typography>
            <Typography variant={"body1"}>
              {item.metadata?.active
                ? t("pages.products.active")
                : t("pages.products.disabled")}
            </Typography>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 1 },
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.presentation.actions")}
            </Typography>
            <Div
              sx={{
                display: "flex",
                flex: { direction: "row", flexWrap: "wrap" },
                minWidth: 70,
              }}
            >
              <Div>
                <IconButton
                  size="small"
                  variant={"contained"}
                  disabled={disabled}
                  onClick={onEdit}
                >
                  <Tooltip title={t("buttons.edit")} placement="top-start">
                    <EditIcon />
                  </Tooltip>
                </IconButton>
                <IconButton
                  size="small"
                  variant={"contained"}
                  disabled={disabled}
                  onClick={() => onDelete(item)}
                >
                  <Tooltip title={t("buttons.delete")} placement="top-start">
                    <DeleteForeverIcon />
                  </Tooltip>
                </IconButton>
              </Div>
            </Div>
          </Div>
        </AccordionSummary>
      </Accordion>
    </Card>
  );
};
/* Todo item prop define */
export default ProductItem;
